import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Descriptions, Switch, Select, InputNumber, Button, Radio, Space, Tag, Tooltip, Popconfirm, Collapse, message } from 'antd';
import apiClient from '../../../shared/services/apiClient';

const MODES = [
    { value: 'grant', label: '赠送本期' },
    { value: 'permanent', label: '永久赠送' },
    { value: 'charge', label: '扣余额' },
];
const MODE_TIP = '赠送本期：现在免费，续费照收；永久赠送：续费也不收；扣余额：同客户购买';

const call = async (method, url, data) => {
    try {
        return (await apiClient[method](url, data)).data;
    } catch (e) {
        return { status: 'error', msg: e.response?.data?.msg || e.message };
    }
};

/** 后台云服务器附加项：IPv6 开关 / 流量费封顶 / 备份附加包。 */
const AdminVmExtrasModal = ({ open, vmId, onClose }) => {
    const [d, setD] = useState(null);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(null);
    const [len, setLen] = useState(64);
    const [adminCap, setAdminCap] = useState(null);
    const [userCap, setUserCap] = useState(null);
    const [points, setPoints] = useState(7);
    const [mode, setMode] = useState('grant');
    const base = `/api/evm/admin/vms/${vmId}`;

    const load = useCallback(async () => {
        setLoading(true);
        const r = await call('get', `${base}/extras`);
        setLoading(false);
        if (r?.status !== 'success') { message.error(r?.msg || '加载失败'); return; }
        setD(r.data);
        setAdminCap(r.data.traffic_cap.admin_cap);
        setUserCap(r.data.traffic_cap.user_cap);
        if (r.data.ipv6.sizes?.length) setLen(r.data.ipv6.sizes.includes(64) ? 64 : r.data.ipv6.sizes[0]);
        if (r.data.backup.packs?.length) setPoints((p) => (r.data.backup.packs.includes(p) ? p : r.data.backup.packs[0]));
    }, [base]);

    useEffect(() => { if (open && vmId) load(); }, [open, vmId, load]);

    const run = async (key, url, data) => {
        setBusy(key);
        const r = await call('post', url, data);
        setBusy(null);
        if (r?.status === 'success') {
            const x = r.data || {};
            message.success(x.invoice_id && mode === 'charge' && key === 'backup'
                ? (x.paid ? `已扣余额（账单 #${x.invoice_id}）` : `余额不足，已生成待付账单 #${x.invoice_id}`) : (r.msg || '已保存'));
            load();
        } else message.error(r?.msg || '操作失败');
    };

    const v6 = d?.ipv6;
    const cap = d?.traffic_cap;
    const bk = d?.backup;
    const v6List = [...(v6?.blocks || []).map((b) => b.prefix), ...(v6?.legacy || []).map((x) => x.address)];

    return (
        <Modal title="附加项" open={open} onCancel={onClose} footer={null} width={620} destroyOnClose loading={loading}>
            {d && (
                <Descriptions column={1} size="small" bordered labelStyle={{ width: 110 }}>
                    <Descriptions.Item label={<Tooltip title="开关后重启实例生效">IPv6</Tooltip>}>
                        <Space wrap>
                            <Switch checked={v6.enabled} loading={busy === 'v6'}
                                onChange={(on) => run('v6', `${base}/ipv6`, { on: on ? 1 : 0, len })} />
                            {!v6.enabled && v6.sizes?.length > 1 && (
                                <Select size="small" value={len} onChange={setLen} style={{ width: 80 }}
                                    options={v6.sizes.map((s) => ({ value: s, label: `/${s}` }))} />
                            )}
                            {v6List.map((p) => <Tag key={p}>{p}</Tag>)}
                            {v6.off && !v6.enabled && <Tag>已关闭</Tag>}
                        </Space>
                    </Descriptions.Item>
                    <Descriptions.Item label={<Tooltip title="空 = 跟随平台；0 = 不封顶">流量费封顶</Tooltip>}>
                        {!cap.has_subscription ? <span style={{ color: '#999' }}>无订阅</span> : (
                            <Space direction="vertical" size={4}>
                                <Space wrap>
                                    <InputNumber size="small" min={0} step={10} value={adminCap} onChange={setAdminCap}
                                        placeholder={`平台 ${cap.platform_cap || '不封顶'}`} style={{ width: 140 }} />
                                    <Button size="small" type="primary" loading={busy === 'cap'}
                                        onClick={() => run('cap', `${base}/traffic-cap`, { admin_cap: adminCap ?? '', user_cap: userCap ?? '' })}>保存</Button>
                                    <span style={{ color: '#999', fontSize: 12 }}>生效 {cap.cap > 0 ? cap.cap : '不封顶'}{cap.enabled ? '' : '（未开流量计费）'}</span>
                                </Space>
                                <Collapse ghost size="small" items={[{
                                    key: 'u', label: '用户自设封顶',
                                    children: <InputNumber size="small" min={1} value={userCap} onChange={setUserCap} placeholder="未设置" style={{ width: 140 }} />,
                                }]} />
                            </Space>
                        )}
                    </Descriptions.Item>
                    <Descriptions.Item label="备份附加包">
                        <Space direction="vertical" size={6}>
                            <Space wrap>
                                {bk.addon_points > 0 && <Tag color="green">附加 {bk.addon_points} 个</Tag>}
                                {bk.override != null && <Tag color="blue">永久 共 {bk.override} 个</Tag>}
                                <span style={{ color: '#999', fontSize: 12 }}>套餐 {bk.plan_points} 个</span>
                            </Space>
                            <Space wrap>
                                <Tooltip title={MODE_TIP}>
                                    <Radio.Group size="small" optionType="button" options={MODES} value={mode} onChange={(e) => setMode(e.target.value)} />
                                </Tooltip>
                                {mode === 'charge' && bk.packs?.length
                                    ? <Select size="small" value={points} onChange={setPoints} style={{ width: 90 }} options={bk.packs.map((n) => ({ value: n, label: `${n} 个` }))} />
                                    : <InputNumber size="small" min={1} max={365} value={points} onChange={setPoints} addonAfter="个" style={{ width: 110 }} />}
                                <Button size="small" type="primary" loading={busy === 'backup'}
                                    onClick={() => run('backup', `${base}/backup-addon`, { points, mode })}>开通</Button>
                                {(bk.addon_points > 0 || bk.override != null) && (
                                    <Popconfirm title="取消备份附加包？" onConfirm={() => run('bkc', `${base}/backup-addon/cancel`)}>
                                        <Button size="small" danger loading={busy === 'bkc'}>取消</Button>
                                    </Popconfirm>
                                )}
                            </Space>
                        </Space>
                    </Descriptions.Item>
                </Descriptions>
            )}
        </Modal>
    );
};

export default AdminVmExtrasModal;
